/**
 * Shipment Status Code Mappings
 * 
 * Maps raw status codes from courier APIs to human-readable display text.
 * Provides consistent status messaging across all couriers.
 */

/**
 * Status code mapping type - allows any string key to map to a string value
 */
export type StatusCodeMap = Record<string, string>;

/**
 * Shipment status code mappings
 * Maps raw courier status codes to display-friendly text
 * 
 * @example
 * statusCodes['pending'] // Returns: "Data Pending"
 * statusCodes['In transit'] // Returns: "In Transit"
 * statusCodes['unknown-status'] // Returns: undefined (use ?? fallback)
 */
export const statusCodes: StatusCodeMap = {
  // Pending states
  pending: 'Data Pending',
  notfound: 'No Data Yet',
  
  // In transit states
  transit: 'In transit',
  'In transit': 'In Transit',
  'At destination sort facility': 'In Transit',
  'Arrived at FedEx location': 'In Transit',
  'International shipment release - Import': 'In Transit',
  'Requested ship date': 'In Transit',
  'At local FedEx facility': 'In Transit',
  'Departed FedEx location': 'In Transit',
  
  // Pickup/delivery states
  pickup: 'Out For Delivery',
  pickedup: 'Picked up',
  OnHandAtOrigin: 'On hand at origin',
  delivered: 'Delivered',
  undelivered: 'Failed Delivery Attempt',
  
  // Exception states
  exception: 'Exception',
  'Shipment exception': 'Exception',
  'Clearance delay - Import': 'Exception',
  
  // Other states
  expired: 'Expired Tracking'
};

/**
 * Get normalized status display text
 * 
 * @param rawStatus - Raw status code from courier API
 * @returns Human-readable status text, or the raw status if no mapping exists
 * 
 * @example
 * getStatusText('pending') // Returns: "Data Pending"
 * getStatusText('unknown') // Returns: "unknown"
 */
export function getStatusText(rawStatus: string): string {
  return statusCodes[rawStatus] ?? rawStatus;
}

/**
 * Check if a status indicates the package is in transit
 * 
 * @param status - Status code to check
 * @returns True if status indicates in-transit state
 */
export function isInTransit(status: string): boolean {
  const normalized = getStatusText(status);
  return normalized === 'In Transit' || normalized === 'In transit';
}

/**
 * Check if a status indicates an exception/problem
 * 
 * @param status - Status code to check
 * @returns True if status indicates an exception
 */
export function isException(status: string): boolean {
  const normalized = getStatusText(status);
  return normalized === 'Exception';
}

/**
 * Check if a status indicates delivery
 * 
 * @param status - Status code to check
 * @returns True if status indicates delivered
 */
export function isDelivered(status: string): boolean {
  const normalized = getStatusText(status);
  return normalized === 'Delivered';
}
